import {
  init as SentryInit,
  browserTracingIntegration,
  captureException,
  httpClientIntegration,
  withScope,
} from '@sentry/react';
import { isBetaDomain, isProdEnvironment } from 'utils/environment';
import { getUserData } from 'utils/functions/getUserData';

// Initialize Sentry
SentryInit({
  dsn: 'https://623e379499964cbb82dff06ade895837@o222076.ingest.sentry.io/5838020',
  environment: getEnvironment(),
  integrations: [
    browserTracingIntegration(),
    httpClientIntegration({ failedRequestStatusCodes: [] }),
  ],
  tracesSampleRate: 0.25,
  denyUrls: [/maze/, /handtalk/, /layers/],
  ignoreErrors: [
    'AuthRefreshError',
    'Token refresh failed',
    'Request call failed',
    'Network Error',
    'Request aborted',
  ],
  beforeSendTransaction: (event) => enrichSentryEvent(event),
  beforeSend: (event) => {
    // store sentry error event to access it from error boundary page
    localStorage.setItem('sentryEvent', String(event?.event_id ?? '--'));

    return enrichSentryEvent(event);
  },
});

function getEnvironment(): string {
  if (isProdEnvironment()) return 'production';
  if (isBetaDomain) return 'beta';

  return 'development';
}

interface SentryEvent {
  tags?: { [key: string]: unknown } | undefined;
}

// add letrus context data to the sentry event
function enrichSentryEvent<Event extends SentryEvent>(event: Event): Event {
  // get user data to enrich sentry event
  const { userRole } = getUserData();

  if (userRole) {
    return {
      ...event,
      tags: {
        ...event?.tags,
        schoolId: userRole?.school,
        schoolName: userRole?.school_name,
        schoolGroupId: userRole?.school_group,
        schoolGradeId: userRole?.school_grade,
        networkId: userRole?.school_network_id,
        schoolGradeName: userRole?.school_grade_name,
        schoolGroupName: userRole?.school_group_name,
      },
    };
  }

  return event;
}

/**
 * Normalize SWR/URLs for grouping in Sentry
 * Ex: /api/user/123/posts?page=1 -> /api/user/:id/posts
 */
export function normalizeApiUrl(key) {
  if (!key) return 'unknown_key';

  // if the key is an array (common in SWR), we take only the first element (URL)
  const url = Array.isArray(key) ? key[0] : key;

  if (typeof url !== 'string') return 'complex_key';

  return url
    .split('?')[0] // 1. Remove Query Params
    .replace(/\/\d+(?=\/|$)/g, '/:id') // 2. Remove numeric IDs between slashes or at the end
    .toLowerCase();
}

// capture API errors
export function captureApiError(error, key) {
  const normalizedKey = normalizeApiUrl(key);

  withScope((scope) => {
    // 1. Identetifies the API endpoint that caused the error
    scope.setTag('api_endpoint', key);

    // 2. If it's an Axios error, extract the request details
    if (error.isAxiosError && error.response) {
      const status = error.response.status;
      const method = error.config?.method?.toUpperCase();

      scope.setTag('status_code', status);
      scope.setTag('method', method);

      // Adds the sent payload and the received response
      scope.setContext('API Details', {
        url: error.config?.url,
        params: error.config?.params,
        request_body: JSON.stringify(error?.config?.data ?? {}, null, 2),
        response_body: JSON.stringify(error?.response?.data ?? {}, null, 2),
      });

      // Creates a unique fingerprint to avoid grouping unrelated errors together
      scope.setFingerprint([
        '{{ default }}',
        String(method),
        String(normalizedKey),
        String(status),
      ]);

      // Define the error level: 5xx is 'error', 4xx is 'warning'
      scope.setLevel(error.response.status >= 500 ? 'error' : 'warning');
    }

    // Since this runs in the SWR flow, the Stack Trace will be much more accurate
    captureException(error);
  });
}
